import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDateTimeUS } from '../utils/dates';
import { apiErrorText, formatMoney, withdrawalState } from '../utils/withdrawal';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';

function Profile() {
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [txError, setTxError] = useState(null);
  const [profile, setProfile] = useState({
    email: '',
    first_name: '',
    last_name: '',
    is_active: false,
    is_staff: false,
    date_joined: '',
    last_login: '',
    balance: 0,
  });
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    zelle: '', 
  });

  const [withdrawalError, setWithdrawalError] = useState(null);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState(null);
  // Пока заявка отправляется, кнопка неактивна: двойной клик давал два запроса
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [totalPending, setTotalPending] = useState(0);

  const [withdrawals, setWithdrawals] = useState([]);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(true);
  const [withdrawalsError, setWithdrawalsError] = useState(null);

  const [updateMessage, setUpdateMessage] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };
  useEffect(() => {
    const fetchTx = async () => {
      try {
        setLoadingTx(true);
        const resp = await fetch('/api/transactions/history/', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!resp.ok) throw new Error('Error fetching transactions');
        const data = await resp.json();
        setTransactions(data);
      } catch (err) {
        setTxError(err.message);
      } finally {
        setLoadingTx(false);
      }
    };
    fetchTx();
  }, [token]);
  // --- Fetch profile ---
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const resp = await fetch('/api/users-logic/me/', {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!resp.ok) throw new Error('Error retrieving user profile.');
        const data = await resp.json();
        setProfile(data);
      } catch (err) {
        setProfileError(err.message);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [token]);

  // --- Fetch withdrawals ---
  const fetchWithdrawals = async () => {
    try {
      setLoadingWithdrawals(true);
      const resp = await fetch('/api/withdrawals/', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) throw new Error('Error fetching withdrawals.');
      const data = await resp.json();

      const pendingAll = data.filter(w => w.status === 'PENDING');
      const sum = pendingAll.reduce((acc, w) => acc + parseFloat(w.amount), 0);
      setTotalPending(sum);
      const last3 = pendingAll
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setWithdrawals(last3);
    } catch (err) {
      setWithdrawalsError(err.message);
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [token]);

  const handleWithdrawalSubmit = async () => {
    if (submittingWithdrawal) return;
    setWithdrawalError(null);
    setWithdrawalSuccess(null);

    const raw = (withdrawalForm.zelle || '').trim();

    const isEmail = /\S+@\S+\.\S+/.test(raw);
    const isPhone = /^[\d\+\-\(\)\s]{7,20}$/.test(raw); 

    if (!isEmail && !isPhone) {
      setWithdrawalError('Please enter a valid phone number or email for Zelle.');
      return;
    }
    const normalizedPhone = isPhone ? raw.replace(/[^\d\+]/g, '') : '';

    const paymentDetails = {
      phone: isPhone ? normalizedPhone : '',
      email: isEmail ? raw : '',
    };

    setSubmittingWithdrawal(true);
    try {
      const resp = await fetch('/api/withdrawals/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: withdrawalForm.amount,
          payment_details: JSON.stringify(paymentDetails),
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(apiErrorText(err, 'Error creating withdrawal'));
      }

      setWithdrawalSuccess('Withdrawal request created.');
      setWithdrawalForm({ amount: '', zelle: '' });
      await fetchWithdrawals();
    } catch (err) {
      setWithdrawalError(err.message);
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  // Обработчик сабмита профиля
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateMessage(null);
    try {
      const resp = await fetch('/api/users-logic/me/', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          first_name: profile.first_name,
          last_name: profile.last_name,
        }),
      });
      if (!resp.ok) throw new Error('Error updating profile.');
      const data = await resp.json();
      setProfile(data);
      setUpdateMessage('Profile updated successfully.');
    } catch (err) {
      setProfileError(err.message);
    }
  };

  // Обработчик изменения полей пароля
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  // Обработчик сабмита формы смены пароля
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);
    setPasswordError(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New password and confirm password do not match.');
      return;
    }

    try {
      const resp = await fetch('/api/users-logic/change-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: passwordData.oldPassword,
          new_password: passwordData.newPassword,
        }),
      });
      if (!resp.ok) throw new Error('Error updating password.');
      setPasswordMessage('Password updated successfully.');
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Loading profile…</div>
      </div>
    );
  }
  if (profileError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">{profileError}</div>
      </div>
    );
  }
  // Те же правила, что проверяет сервер (см. utils/withdrawal.js)
  const {
    available: availableToWithdraw,
    disabled: withdrawalBlocked,
    reason: disabledReason,
  } = withdrawalState({
    balance: profile.balance,
    pending: totalPending,
    amount: withdrawalForm.amount,
    zelle: withdrawalForm.zelle,
  });
  const isDisabled = withdrawalBlocked || submittingWithdrawal;
  return (
  <div className="flex flex-col min-h-screen bg-white">
    <HeaderLogged />

    <main className="flex-1 bg-white">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Верхний блок: заголовок */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-[#0F2C5E]">
            My Profile
          </h2>
          {/* Если нужны кнопки рядом с заголовком — вставляйте здесь */}
          <div className="flex space-x-3">
            {/* <button className="bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition text-sm">
               Example
             </button> */}
          </div>
        </div>

        <hr className="border-t border-gray-300" />

        {/* Двухколоночный макет */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Левая колонка */}
          <div className="space-y-6">
            {/* Profile Info */}
            <div className="bg-white p-6 rounded shadow">
              <h3 className="text-2xl font-semibold mb-4">
                Profile Information
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    disabled
                    className="w-full mt-1 p-2 border rounded bg-gray-50"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={profile.first_name}
                    onChange={handleChange}
                    className="w-full mt-1 p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={profile.last_name}
                    onChange={handleChange}
                    className="w-full mt-1 p-2 border rounded"
                  />
                </div>
                <button
                  type="submit"
                  className="mt-4 w-full bg-[#0F2C5E] text-white py-2 rounded hover:bg-[#0F2C5E]/80 transition text-sm"
                >
                  Update Profile
                </button>
              </form>
            </div>

            {/* Change Password */}
            <div className="bg-white p-6 rounded shadow">
              <h3 className="text-2xl font-semibold mb-4">
                Change Password
              </h3>
              {!showPasswordForm ? (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-[#0F2C5E]/80 transition text-sm"
                >
                  Change Password
                </button>
              ) : (
                <form onSubmit={handlePasswordSubmit}>
                  {passwordMessage && (
                    <div className="mb-4 p-3 bg-green-100 text-green-800 rounded">
                      {passwordMessage}
                    </div>
                  )}
                  {passwordError && (
                    <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
                      {passwordError}
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="oldPassword"
                      value={passwordData.oldPassword}
                      onChange={handlePasswordChange}
                      className="w-full mt-1 p-2 border rounded"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full mt-1 p-2 border rounded"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full mt-1 p-2 border rounded"
                    />
                  </div>
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      className="bg-[#0F2C5E] text-white px-4 py-2 rounded hover:bg-[#0F2C5E]/80 transition text-sm"
                    >
                      Update Password
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPasswordForm(false)}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Правая колонка */}
          <div className="bg-white p-6 rounded shadow">
            <h3 className="text-2xl font-semibold mb-4">My Rewards</h3>

            {/* Одна цифра вводила в заблуждение: висящая заявка уже резервирует
                часть денег, и запросить можно только остаток. */}
            <div className="mb-6 space-y-1">
              <p className="text-lg">
                <span className="font-medium">Your rewards:</span>{' '}
                <span className="text-[#0F2C5E]">{formatMoney(profile.balance)}</span>
              </p>
              {totalPending > 0 && (
                <p className="text-sm text-gray-600">
                  Pending withdrawal: &minus;{formatMoney(totalPending)}
                </p>
              )}
              <p className="text-lg">
                <span className="font-medium">Available to withdraw:</span>{' '}
                <span className="text-[#0F2C5E] font-semibold">{formatMoney(availableToWithdraw)}</span>
              </p>
            </div>

            <h4 className="text-xl font-semibold mb-2">
              Your withdrawal requests
            </h4>

            {loadingWithdrawals ? (
              <p>Loading…</p>
            ) : withdrawalsError ? (
              <p className="text-red-500">{withdrawalsError}</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="py-2 text-left">Date</th>
                    <th className="py-2 text-left">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.length > 0 ? (
                    withdrawals.map(w => (
                      <tr key={w.id} className="border-b">
                        <td className="py-2">
                          {fmtDateTimeUS(w.created_at)}
                        </td>
                        <td className="py-2">${w.amount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="2"
                        className="py-4 text-center text-gray-500"
                      >
                        No pending requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            <div className="mt-6 space-y-4">
              {withdrawalError && (
                <div className="p-2 bg-red-100 text-red-800 rounded">
                  {withdrawalError}
                </div>
              )}
              {withdrawalSuccess && (
                <div className="p-2 bg-green-100 text-green-800 rounded">
                  {withdrawalSuccess}
                </div>
              )}
              <div className="grid grid-cols-1 gap-3">
                <input
                  type="number"
                  name="amount"
                  min={100}
                  value={withdrawalForm.amount}
                  onChange={e =>
                    setWithdrawalForm(f => ({ ...f, amount: e.target.value }))
                  }
                  placeholder="Amount to withdraw"
                  className="w-full p-2 border rounded"
                />

                <div>
                  <label className="block text-gray-700 mb-1">Enter your Zelle info</label>
                  <input
                    type="text"
                    name="zelle"
                    value={withdrawalForm.zelle}
                    onChange={e => setWithdrawalForm(f => ({ ...f, zelle: e.target.value }))}
                    placeholder="Phone number or email"
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    You can enter either a phone number (e.g. +1 555 555 5555) or an email address.
                  </p>
                </div>
              </div>

            <div className="w-full">
              <button
                onClick={handleWithdrawalSubmit}
                disabled={isDisabled}
                className={`w-full py-2 rounded text-white ${
                  isDisabled
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#0F2C5E] hover:bg-[#0F2C5E]/80'
                } transition text-sm`}
              >
                {submittingWithdrawal ? 'Sending…' : 'Request Withdrawal'}
              </button>

              {/* Причина — текстом под кнопкой: подсказку при наведении клиент не
                  находил, а на телефоне её не увидеть вовсе. */}
              {withdrawalBlocked && disabledReason && (
                <p className="text-sm text-gray-600 mt-2" role="status">
                  {disabledReason}
                </p>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* История транзакций */}
        <div className="mt-12 bg-white p-6 rounded shadow overflow-x-auto">
          <h3 className="text-2xl font-semibold mb-4">
            Transaction History
          </h3>

          {loadingTx ? (
            <p>Loading history…</p>
          ) : txError ? (
            <p className="text-red-500">{txError}</p>
          ) : (
            <div className="overflow-y-auto max-h-48">
              <table className="min-w-full text-sm">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => {
                    const isCredit = tx.tx_type === 'CREDIT';
                    return (
                      <tr
                        key={tx.id}
                        className="border-b hover:bg-gray-100"
                      >
                        <td className="px-4 py-2">
                          {fmtDateTimeUS(tx.created_at)}
                        </td>
                        <td className="px-4 py-2">
                          {isCredit ? 'Reward' : 'Withdrawal'}
                        </td>
                        <td
                          className={`px-4 py-2 text-right ${
                            isCredit
                              ? 'text-green-600'
                              : 'text-gray-600'
                          }`}
                        >
                          {isCredit ? '+' : '-'}${parseFloat(
                            tx.amount
                          ).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>

    <Footer />
  </div>
);
}

export default Profile;
